import React from 'react';
import * as Sentry from '@sentry/react';

type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  error: Error | null;
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Application render error', error, errorInfo);
    Sentry.withScope((scope) => {
      scope.setExtras(errorInfo as unknown as Record<string, unknown>);
      Sentry.captureException(error);
    });
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    const isChunkLoadError =
      /loading chunk|failed to fetch dynamically imported module|importing a module script failed/i.test(
        this.state.error.message || '',
      );

    return (
      <div className="flex min-h-screen items-center justify-center bg-muted px-4">
        <section className="w-full max-w-md rounded-2xl border border-border bg-background p-6 text-center shadow-sm">
          <h1 className="text-xl font-bold text-foreground">We could not load this page</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isChunkLoadError
              ? 'A new version of the app may have been deployed while your browser was still using old files.'
              : 'Something went wrong while opening this page.'}
          </p>
          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={() => {
                window.location.href = '/';
              }}
              className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted"
            >
              Go home
            </button>
          </div>
        </section>
      </div>
    );
  }
}

export default ErrorBoundary;
